import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '@core/services/auth/auth.service';
import { User } from '@core/models/user.model';
import { ParticipationResponse } from '@core/models/participation.response.model';
import { ApiResponse } from '@core/models/common/api-response.model';
import { ParticipantRequest } from '@core/models/participant.request.model';
import { ApiResponseHandler } from '@core/handlers/api-response.handler';
import { NextItem } from '../../models/common/next-item.model';
import { UserParticipation } from '@core/models/user-participation.response.model';
import { PushLiveParticipation } from '@core/models/push-live-participation.model';
import { APP_CONFIG } from '@core/config/app-config.token';
import { AppConfig } from '@core/config/app-config.model';

@Injectable({
  providedIn: 'root',
})
export class ParticipationService {
  private conferenceUrl!: string;
  private courseUrl!: string;
  private pushLiveUrl!: string;
  private storePushLiveParticipationUrl!: string;
  private storeConferenceParticipationUrl!: string;
  private storeCourseParticipationUrl!: string;
  private userParticipantUrl!: string;
  private conferenceUserParticipantUrl!: string;
  private pushLiveUserParticipantUrl!: string;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    @Inject(APP_CONFIG) private readonly cfg: AppConfig
  ) {
    this.conferenceUrl = `${this.cfg.serviceApiUrl}/conferences`;
    this.courseUrl = `${this.cfg.serviceApiUrl}/courses`;
    this.pushLiveUrl = `${this.cfg.serviceApiUrl}/push-live`;
    this.storeConferenceParticipationUrl = `${this.conferenceUrl}/participation`;
    this.storeCourseParticipationUrl = `${this.courseUrl}/participation`;
    this.storePushLiveParticipationUrl = `${this.pushLiveUrl}/participation`;
    this.userParticipantUrl = `${this.courseUrl}`;
    this.conferenceUserParticipantUrl = `${this.conferenceUrl}`;
    this.pushLiveUserParticipantUrl = `${this.pushLiveUrl}`;
  }

  getAuthenticatedUser(): User | null {
    return this.authService.getUser();
  }

  fetchParticipationDetailsForConference(
    conferenceUuid: string
  ): Observable<ParticipationResponse> {
    return this.http
      .get<ParticipationResponse>(`${this.conferenceUrl}/${conferenceUuid}/participation`)
      .pipe(catchError(ApiResponseHandler.handleError));
  }

  submitParticipationForConference(
    formData: ParticipantRequest
  ): Observable<ApiResponse<NextItem>> {
    return this.http
      .post<ApiResponse<NextItem>>(this.storeConferenceParticipationUrl, formData)
      .pipe(catchError(ApiResponseHandler.handleError));
  }

  fetchParticipationDetailsForCourse(courseUuid: string): Observable<ParticipationResponse> {
    return this.http
      .get<ParticipationResponse>(`${this.courseUrl}/${courseUuid}/participation`)
      .pipe(catchError(ApiResponseHandler.handleError));
  }

  fetchParticipationDetailsForPushLive(pushLiveUuid: string): Observable<ParticipationResponse> {
    return this.http
      .get<ParticipationResponse>(`${this.pushLiveUrl}/${pushLiveUuid}/participation`)
      .pipe(catchError(ApiResponseHandler.handleError));
  }

  submitParticipationForCourse(formData: ParticipantRequest): Observable<ApiResponse<NextItem>> {
    return this.http
      .post<ApiResponse<NextItem>>(this.storeCourseParticipationUrl, formData)
      .pipe(catchError(ApiResponseHandler.handleError));
  }

  submitParticipationForPushLive(
    formData: ParticipantRequest
  ): Observable<ApiResponse<PushLiveParticipation>> {
    return this.http
      .post<ApiResponse<PushLiveParticipation>>(this.storePushLiveParticipationUrl, formData)
      .pipe(catchError(ApiResponseHandler.handleError));
  }

  getUserParticipationDetails(courseUuid: string): Observable<ApiResponse<UserParticipation>> {
    return this.http
      .get<
        ApiResponse<UserParticipation>
      >(`${this.userParticipantUrl}/${courseUuid}/user-participation`)
      .pipe(catchError(ApiResponseHandler.handleError));
  }

  getConferenceUserParticipationDetails(
    liveUuid: string
  ): Observable<ApiResponse<UserParticipation>> {
    return this.http
      .get<
        ApiResponse<UserParticipation>
      >(`${this.conferenceUserParticipantUrl}/${liveUuid}/user-participation`)
      .pipe(catchError(ApiResponseHandler.handleError));
  }

  getPushLiveUserParticipationDetails(
    pushLiveUuid: string
  ): Observable<ApiResponse<UserParticipation>> {
    return this.http
      .get<
        ApiResponse<UserParticipation>
      >(`${this.pushLiveUserParticipantUrl}/${pushLiveUuid}/user-participation`)
      .pipe(catchError(ApiResponseHandler.handleError));
  }
}
